/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FlexColumn, FlexRow } from '../GridsAndLayouts';
import Typography from '../Typography';
import './FeedStepper.scss';

const TONE_BG_CLASS = {
  green: 'ds-bg-pal-green-1000',
  primary: 'ds-bg-pal-purple-1000',
  neutral: 'ds-bg-pal-grey-slate-1000',
};

function resolveToneBgClass(tone) {
  return TONE_BG_CLASS[tone] || TONE_BG_CLASS.primary;
}

function defaultFillPercent(activeIndex, stepCount) {
  if (stepCount <= 0) return 0;
  if (stepCount === 1) return 100;
  return Math.min(100, Math.max(0, ((activeIndex + 0.38) / stepCount) * 100));
}

function resolveStepState(index, safeIndex) {
  return {
    done: index < safeIndex,
    active: index === safeIndex,
    upcoming: index > safeIndex,
  };
}

function resolveRootClassName({ className, isVertical, nodeVariant, tone }) {
  return classNames(
    'ds-feed-stepper',
    isVertical && 'ds-feed-stepper--vertical',
    nodeVariant === 'outline' && 'ds-feed-stepper--node-outline',
    nodeVariant === 'outline' && `ds-feed-stepper--tone-${tone}`,
    className,
  );
}

function FeedStepperNode({
  step,
  index,
  done,
  active,
  upcoming,
  toneBgClass,
  useCustomAccent,
  accent,
  nodeVariant = 'filled',
}) {
  const nodeClass = classNames('ds-feed-stepper__node', {
    'ds-feed-stepper__node--done': done,
    'ds-feed-stepper__node--active': active,
    'ds-feed-stepper__node--upcoming': upcoming,
  });
  const showFill = nodeVariant === 'filled' && (done || active);

  return (
    <FlexColumn
      className={classNames(nodeClass, showFill && !useCustomAccent && toneBgClass)}
      align="center"
      justify="center"
      inline
      fullWidth={false}
      style={showFill && useCustomAccent ? { background: accent } : undefined}
    >
      {step.marker != null ? step.marker : index + 1}
    </FlexColumn>
  );
}

FeedStepperNode.propTypes = {
  step: PropTypes.shape({
    marker: PropTypes.node,
  }).isRequired,
  index: PropTypes.number.isRequired,
  done: PropTypes.bool.isRequired,
  active: PropTypes.bool.isRequired,
  upcoming: PropTypes.bool.isRequired,
  toneBgClass: PropTypes.string.isRequired,
  useCustomAccent: PropTypes.bool.isRequired,
  accent: PropTypes.string,
  nodeVariant: PropTypes.oneOf(['filled', 'outline']),
};

function FeedStepperLabels({ step, done, active, upcoming, vertical = false }) {
  const labelClass = classNames('ds-feed-stepper__label', {
    'ds-feed-stepper__label--active': done || active,
    'ds-feed-stepper__label--muted': upcoming,
  });

  if (vertical) {
    return (
      <FlexColumn align="start" fullWidth={false} gap="var(--ds-space-1)" className="ds-feed-stepper__content">
        <Typography
          component="div"
          variant="bodyMedium"
          fontWeight={active ? 'semibold' : 'regular'}
          className={labelClass}
        >
          {step.label}
        </Typography>
        {step.description != null && step.description !== '' ? (
          <Typography component="div" variant="bodySmall" color="ui-subtle" className="ds-feed-stepper__description">
            {step.description}
          </Typography>
        ) : null}
      </FlexColumn>
    );
  }

  return (
    <Typography component="div" variant="bodySmall" fontWeight="medium" className={labelClass}>
      {step.label}
    </Typography>
  );
}

FeedStepperLabels.propTypes = {
  step: PropTypes.shape({
    label: PropTypes.node.isRequired,
    description: PropTypes.node,
  }).isRequired,
  done: PropTypes.bool.isRequired,
  active: PropTypes.bool.isRequired,
  upcoming: PropTypes.bool.isRequired,
  vertical: PropTypes.bool,
};

function FeedStepperNodeSlot(props) {
  const {
    step,
    index,
    safeIndex,
    toneBgClass,
    useCustomAccent,
    accent,
    nodeVariant,
    isLast,
    isVertical,
    activeStepComplete,
  } = props;
  const { done, active, upcoming } = resolveStepState(index, safeIndex);
  const connectorFilled = index < safeIndex || (activeStepComplete && index === safeIndex);

  const node = (
    <FeedStepperNode
      step={step}
      index={index}
      done={done}
      active={active}
      upcoming={upcoming}
      toneBgClass={toneBgClass}
      useCustomAccent={useCustomAccent}
      accent={accent}
      nodeVariant={nodeVariant}
    />
  );

  if (!isVertical) {
    return node;
  }

  return (
    <FlexColumn align="center" fullWidth={false} className="ds-feed-stepper__rail" inline>
      {node}
      {!isLast ? (
        <span
          className={classNames('ds-feed-stepper__connector', {
            'ds-feed-stepper__connector--filled': connectorFilled && !useCustomAccent,
            [toneBgClass]: connectorFilled && !useCustomAccent,
          })}
          style={connectorFilled && useCustomAccent ? { background: accent } : undefined}
          aria-hidden
        />
      ) : null}
    </FlexColumn>
  );
}

FeedStepperNodeSlot.propTypes = {
  step: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    marker: PropTypes.node,
  }).isRequired,
  index: PropTypes.number.isRequired,
  safeIndex: PropTypes.number.isRequired,
  toneBgClass: PropTypes.string.isRequired,
  useCustomAccent: PropTypes.bool.isRequired,
  accent: PropTypes.string,
  nodeVariant: PropTypes.oneOf(['filled', 'outline']),
  isLast: PropTypes.bool.isRequired,
  isVertical: PropTypes.bool.isRequired,
  activeStepComplete: PropTypes.bool,
};

const FeedStepper = ({
  steps,
  activeIndex = 0,
  activeStepComplete = false,
  tone = 'primary',
  accent,
  trackFillPercent,
  orientation = 'horizontal',
  nodeVariant = 'filled',
  className = '',
  ...rest
}) => {
  const useCustomAccent = Boolean(accent);
  const toneBgClass = resolveToneBgClass(tone);
  const n = steps?.length ?? 0;
  const safeIndex = Math.min(Math.max(0, activeIndex), Math.max(0, n - 1));
  const isVertical = orientation === 'vertical';
  const rootClassName = resolveRootClassName({ className, isVertical, nodeVariant, tone });

  let fillPct = defaultFillPercent(safeIndex, n);
  if (typeof trackFillPercent === 'number') {
    fillPct = Math.min(100, Math.max(0, trackFillPercent));
  }

  const nodeSlotProps = {
    safeIndex,
    toneBgClass,
    useCustomAccent,
    accent,
    nodeVariant,
    isVertical,
    activeStepComplete,
  };

  if (isVertical) {
    return (
      <FlexColumn className={rootClassName} align="stretch" fullWidth={false} {...rest}>
        <FlexColumn className="ds-feed-stepper__steps" role="list" align="stretch" fullWidth={false}>
          {steps.map((step, i) => {
            const { done, active, upcoming } = resolveStepState(i, safeIndex);

            return (
              <FlexRow
                key={step.key ?? i}
                className="ds-feed-stepper__step"
                role="listitem"
                align="stretch"
                fullWidth={false}
                gap="var(--ds-space-4)"
              >
                <FeedStepperNodeSlot step={step} index={i} isLast={i === n - 1} {...nodeSlotProps} />
                <FeedStepperLabels step={step} done={done} active={active} upcoming={upcoming} vertical />
              </FlexRow>
            );
          })}
        </FlexColumn>
      </FlexColumn>
    );
  }

  return (
    <FlexColumn className={rootClassName} align="stretch" fullWidth {...rest}>
      <FlexRow className="ds-feed-stepper__track" aria-hidden>
        <FlexRow
          className={classNames('ds-feed-stepper__track-fill', !useCustomAccent && toneBgClass)}
          style={{
            display: 'block',
            width: `${fillPct}%`,
            ...(useCustomAccent ? { background: accent } : {}),
            height: 'var(--ds-space-1)',
            maxHeight: 'var(--ds-space-1)',
          }}
          inline
          fullWidth={false}
        />
      </FlexRow>
      <FlexRow className="ds-feed-stepper__steps" role="list" justify="between" align="start" fullWidth>
        {steps.map((step, i) => {
          const { done, active, upcoming } = resolveStepState(i, safeIndex);

          return (
            <FlexColumn key={step.key ?? i} className="ds-feed-stepper__step" role="listitem" align="center" gap={10}>
              <FeedStepperNodeSlot step={step} index={i} isLast={i === n - 1} {...nodeSlotProps} />
              <FeedStepperLabels step={step} done={done} active={active} upcoming={upcoming} />
            </FlexColumn>
          );
        })}
      </FlexRow>
    </FlexColumn>
  );
};

const stepShape = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.node.isRequired,
  description: PropTypes.node,
  marker: PropTypes.node,
});

FeedStepper.propTypes = {
  steps: PropTypes.arrayOf(stepShape).isRequired,
  activeIndex: PropTypes.number,
  /** When true, the connector below the active step is filled (e.g. step content finished, Continue enabled). */
  activeStepComplete: PropTypes.bool,
  tone: PropTypes.oneOf(['green', 'primary', 'neutral']),
  accent: PropTypes.string,
  trackFillPercent: PropTypes.number,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  /** `filled` — solid tone background (default). `outline` — light background with border (L&D sidebar). */
  nodeVariant: PropTypes.oneOf(['filled', 'outline']),
  className: PropTypes.string,
};

export { FeedStepper };
export default FeedStepper;
